import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';
import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { APP_CONFIG, AppConfig } from '../config/app-config.token';

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {

    public themeIcon: ThemeIcons = ThemeIcons['light-theme'];
    public userTheme: Themes = Themes['light-theme'];
    public themeChange$ = new BehaviorSubject<Themes>(Themes['default-theme']);
    public isSmallDisplay$ = new BehaviorSubject<boolean>(false);
    public isSmallDisplay = false;

    private brandClass: string | null = null;
    // With SSR, localStorage is not available on the server.
    private localStorageRef: Storage | undefined = this.document.defaultView?.localStorage;
    private onResizeSubject = new Subject<number>();
    private destroy$ = new Subject<void>();

    constructor(
        private overlay: OverlayContainer,
        @Inject(DOCUMENT) private document: Document,
        @Optional() @Inject(APP_CONFIG) appConfig: AppConfig | null
    ) {
        this.brandClass = appConfig ? `brand-${appConfig.brandTheme}` : null;

        this.userTheme = this.localStorageRef?.getItem('theme') as Themes || Themes['light-theme'];
        if (this.userTheme === Themes['light-theme']) {
            this.themeIcon = ThemeIcons['light-theme'];
        } else if (this.userTheme === Themes['dark-theme']) {
            this.themeIcon = ThemeIcons['dark-theme'];
        }

        this.themeChange$.next(this.userTheme);
        this.applyTheme(this.userTheme);

        this.onResizeSubject
            .pipe(debounceTime(200), takeUntil(this.destroy$))
            .subscribe(windowWidth => {
                const hasNewWindowWidth = (windowWidth < 768);
                if (hasNewWindowWidth !== this.isSmallDisplay) {
                    this.isSmallDisplay = hasNewWindowWidth;
                    this.isSmallDisplay$.next(this.isSmallDisplay);
                }
            });
    }

    public getThemeIcon(): ThemeIcons {
        return this.themeIcon;
    }

    public toggleTheme(): ThemeIcons {
        if (!this.document) return this.themeIcon;

        const newTheme = this.userTheme === Themes['light-theme'] ? Themes['dark-theme'] : Themes['light-theme'];

        if (newTheme === Themes['light-theme']) {
            this.themeIcon = ThemeIcons['light-theme'];
        } else if (newTheme === Themes['dark-theme']) {
            this.themeIcon = ThemeIcons['dark-theme'];
        }

        this.applyTheme(newTheme);

        this.userTheme = newTheme;
        if (this.localStorageRef) this.localStorageRef.setItem('theme', newTheme);

        this.themeChange$.next(newTheme);

        return this.themeIcon;
    }

    private applyTheme(theme: Themes) {
        try {
            const overlayClasses = this.overlay.getContainerElement().classList;
            const bodyClasses = this.document?.body?.classList;

            // Remove light/dark/default variant classes
            (['default-theme', 'light-theme', 'dark-theme'] as const).forEach(cls => {
                overlayClasses.remove(cls);
                bodyClasses?.remove(cls);
            });

            // Always ensure the brand class is present
            if (this.brandClass) {
                overlayClasses.add(this.brandClass);
                bodyClasses?.add(this.brandClass);
            }

            overlayClasses.add(theme);
            bodyClasses?.add(theme);
        } catch (error) {
            console.error('Error applying theme: ' + error);
        }
    }

    public onResize(innerWidth?: number) {
        if (innerWidth === undefined) innerWidth = this.document.defaultView?.innerWidth || 0;
        this.onResizeSubject.next(innerWidth);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}


export enum Themes {
    'default-theme' = 'default-theme',
    'light-theme' = 'light-theme',
    'dark-theme' = 'dark-theme'
}

export enum ThemeIcons {
    'default-theme' = 'flare',
    'light-theme' = 'wb_sunny',
    'dark-theme' = 'dark_mode'
}
